import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertTriangle,
  Gauge,
  Activity,
  CheckCircle,
} from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { KpiStatCard } from "@/components/shared/KpiStatCard";
import { Badge } from "@/components/ui/badge";

interface ProductionTabProps {
  isLoading: boolean;
  kpis: any;
  quickStats: any;
  earlyWarnings?: any;
  onDrillDown?: (config: { type: string; title: string; status?: string }) => void;
}

export function ProductionTab({
  isLoading,
  kpis,
  quickStats,
  earlyWarnings,
  onDrillDown,
}: ProductionTabProps) {
  const prodAlerts = earlyWarnings?.production || {};
  const delayedPOCount = prodAlerts.delayedPOCount ?? 0;
  const stoppedMachinesCount = prodAlerts.stoppedMachinesCount ?? 0;
  const scrapRate = prodAlerts.scrapRate ?? 0;

  // Extract real production KPIs from backend
  const prodOee = kpis?.production?.oee || {};
  const { oee = 0, availability = 0, performance = 0, quality = 0, metrics = {} } = prodOee;
  const { totalQtyOk = 0, totalQtyNg = 0 } = metrics;
  
  // Bar chart for OEE components
  const oeeChartData = [
    { metric: "Khả dụng (A)", value: availability, fill: "#3b82f6" },
    { metric: "Hiệu suất (P)", value: performance, fill: "#f59e0b" },
    { metric: "Chất lượng (Q)", value: quality, fill: "#8b5cf6" },
    { metric: "OEE Tổng", value: oee, fill: "#10b981" },
  ];

  // Map real defect data from backend
  const topNGRates = kpis?.production?.bottlenecks?.topNGRates || [];
  const defectData = topNGRates.length > 0 ? topNGRates.map((b: any) => ({
    reason: b.stepId || 'Unknown',
    count: b.ngRate || 0,
  })) : [
    { reason: "Chưa có phát sinh", count: 0 }
  ];

  const {
    runningMachines = 0,
    idleMachines = 0,
    maintenanceMachines = 0,
    errorMachines = 0,
  } = quickStats?.production || {};

  return (
    <div className="space-y-3">
      {/* Cảnh báo vận hành sản xuất */}
      <div className="flex flex-wrap items-center gap-2 p-2.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-xs">
        <span className="font-bold text-rose-700 flex items-center gap-1.5 shrink-0">
          <AlertTriangle className="h-3.5 w-3.5" />
          Cảnh báo sản xuất:
        </span>
        <div className="flex flex-wrap items-center gap-2 text-foreground">
          {delayedPOCount > 0 ? (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "delayed_pos",
                  title: "Lệnh SX chậm tiến độ",
                })
              }
              className="bg-card border-rose-300 text-rose-800 font-semibold cursor-pointer hover:bg-rose-100 transition-colors"
            >
              {`🚨 ${delayedPOCount} Lệnh SX chậm tiến độ`}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-card border-emerald-300 text-emerald-700 font-semibold">
              ✓ Lệnh SX đúng tiến độ
            </Badge>
          )}

          {stoppedMachinesCount > 0 ? (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "stopped_machines",
                  title: "Máy dừng hoạt động",
                })
              }
              className="bg-card border-border text-foreground font-semibold cursor-pointer hover:bg-muted transition-colors"
            >
              {`⚠️ ${stoppedMachinesCount} Máy dừng / bảo trì`}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-card border-border text-foreground font-semibold">
              ✓ Thiết bị ổn định
            </Badge>
          )}

          <Badge
            variant="outline"
            className="bg-card border-emerald-200 text-emerald-700 font-semibold"
          >
            {`▲ Tỷ lệ phế phẩm ${scrapRate}%`}
          </Badge>
        </div>
      </div>

      {/* ═══ 4 THẺ KPI SẢN XUẤT (KpiStatCard & Chuẩn PoP) ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiStatCard
          title="Hiệu suất OEE"
          value={oee}
          formatType="percent"
          colorVariant="emerald"
          icon={Gauge}
          badgeText="Tổng hợp"
          trendLabel={scrapRate > 0 ? `Tỷ lệ phế phẩm ${scrapRate}%` : "Chất lượng cao"}
          direction="positive"
          isLoading={isLoading}
          testId="prod-kpi-oee"
        />

        <KpiStatCard
          title="Máy đang chạy"
          value={runningMachines}
          unit="máy"
          formatType="number"
          colorVariant="blue"
          icon={Activity}
          badgeText="Thời gian thực"
          trendLabel={`${idleMachines} chờ • ${maintenanceMachines + errorMachines} dừng/sự cố`}
          direction="positive"
          isLoading={isLoading}
          testId="prod-kpi-machines"
        />

        <KpiStatCard
          title="Sản lượng đạt"
          value={totalQtyOk}
          unit="sp"
          formatType="number"
          colorVariant="indigo"
          icon={CheckCircle}
          badgeText="Đạt KCS"
          trendLabel={`KCS: ${totalQtyOk.toLocaleString()} sản phẩm`}
          direction="positive"
          isLoading={isLoading}
          testId="prod-kpi-qty-ok"
        />

        <KpiStatCard
          title="Phế phẩm"
          value={totalQtyNg}
          unit="sp"
          formatType="number"
          colorVariant="rose"
          icon={AlertTriangle}
          badgeText={totalQtyOk + totalQtyNg > 0 ? `Tỷ lệ ${((totalQtyNg / (totalQtyOk + totalQtyNg)) * 100).toFixed(1)}%` : "0%"}
          trendLabel={scrapRate > 2.0 ? "Cần kiểm soát hao hụt" : "Trong ngưỡng an toàn"}
          direction="inverse"
          isLoading={isLoading}
          testId="prod-kpi-qty-ng"
        />
      </div>

      {/* Biểu đồ OEE & Trạng thái thiết bị (Tối giản) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* OEE Component Chart */}
        <Card className="lg:col-span-2 shadow-xs border-border">
          <CardHeader className="py-2.5 px-3">
            <CardTitle className="text-xs font-bold text-foreground uppercase tracking-wide">
              Thành phần OEE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="h-[240px] w-full">
              <ResponsiveContainer 
                width="100%" 
                height={240} 
                minWidth={1} 
                minHeight={1}
                initialDimension={{ width: 100, height: 100 }}
              >
                <BarChart data={oeeChartData} margin={{ top: 10, right: 20, left: -20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="metric" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ fill: "#f1f5f9" }}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    formatter={(value) => [`${value}%`, ""]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {oeeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Trạng thái thiết bị & Phân bố phế phẩm */}
        <div className="space-y-3 flex flex-col">
          <Card className="shadow-xs border-border flex-1">
            <CardHeader className="py-2.5 px-3">
              <CardTitle className="text-xs font-bold text-foreground uppercase tracking-wide">
                Trạng thái thiết bị
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                    <span className="text-muted-foreground font-medium">Đang chạy</span>
                  </div>
                  <span className="font-bold text-foreground font-mono">{runningMachines} máy</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-400"></div>
                    <span className="text-muted-foreground font-medium">Đang chờ</span>
                  </div>
                  <span className="font-bold text-foreground font-mono">{idleMachines} máy</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                    <span className="text-muted-foreground font-medium">Bảo trì</span>
                  </div>
                  <span className="font-bold text-foreground font-mono">{maintenanceMachines} máy</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-rose-500"></div>
                    <span className="text-muted-foreground font-medium">Sự cố</span>
                  </div>
                  <span className="font-bold text-foreground font-mono">{errorMachines} máy</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xs border-border bg-card flex-1">
            <CardHeader className="py-2.5 px-3">
              <CardTitle className="text-xs font-bold text-foreground uppercase tracking-wide">
                Hao hụt công đoạn
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="h-[120px] w-full">
                <ResponsiveContainer 
                  width="100%" 
                  height={120} 
                  minWidth={1} 
                  minHeight={1}
                  initialDimension={{ width: 100, height: 100 }}
                >
                  <BarChart data={defectData} layout="vertical" margin={{ top: 0, right: 10, left: -15, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="reason" type="category" axisLine={false} tickLine={false} fontSize={10} stroke="#64748b" />
                    <Tooltip
                      cursor={{ fill: "#f1f5f9" }}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }}
                      formatter={(value) => [`${value}%`, "Tỷ lệ NG"]}
                    />
                    <Bar dataKey="count" fill="#f43f5e" radius={[0, 4, 4, 0]}>
                      {defectData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? "#e11d48" : "#fb7185"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
